import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import socket from '../utils/socket';
import { EmptyState, Badge, sessionStatusBadgeMap, sessionTypeBadgeMap, attendanceBadgeMap, conflictBadgeMap, Modal, useSort, SortTh, TableSkeletonRows } from './UI';
import { fmtDate, fmtDateTime } from '../utils/format';
import { getHoliday } from '../utils/sgHolidays';

export function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Returns the Mon–Sun week containing `anchor` (defaults to today).
export function getWeekRange(anchor = new Date()) {
  const d = new Date(anchor);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d); monday.setDate(d.getDate() + diffToMonday);
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  return { from: ymd(monday), to: ymd(sunday), monday, sunday };
}

// Fetch range + display info for each view mode. `week` keeps the existing
// Mon-Sun range (grid itself only shows Mon-Fri columns); `month` widens the
// fetch to the full calendar-grid range (including the leading/trailing days
// from adjoining months needed to fill out full weeks) so those spillover
// cells aren't left silently blank.
export function getRangeForMode(mode, anchor = new Date()) {
  if (mode === 'day') {
    const key = ymd(anchor);
    return { from: key, to: key };
  }
  if (mode === 'month') {
    const weeks = getMonthWeeks(anchor);
    return { from: weeks[0][0].key, to: weeks[weeks.length - 1][6].key, weeks };
  }
  return getWeekRange(anchor);
}

// Advances `anchor` by one unit of `mode` (or `-1` unit when dir is -1).
export function shiftAnchor(mode, anchor, dir) {
  const next = new Date(anchor);
  if (mode === 'day') next.setDate(next.getDate() + dir);
  else if (mode === 'month') next.setMonth(next.getMonth() + dir);
  else next.setDate(next.getDate() + dir * 7);
  return next;
}

// Full calendar weeks (Mon-Sun) needed to display every day of anchor's month.
export function getMonthWeeks(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const startDay = first.getDay();
  const gridStart = new Date(first); gridStart.setDate(first.getDate() + (startDay === 0 ? -6 : 1 - startDay));
  const endDay = last.getDay();
  const gridEnd = new Date(last); gridEnd.setDate(last.getDate() + (endDay === 0 ? 0 : 7 - endDay));

  const weeks = [];
  const cursor = new Date(gridStart);
  while (cursor <= gridEnd) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push({ date: new Date(cursor), key: ymd(cursor), inMonth: cursor.getMonth() === anchor.getMonth() });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

function fmtTime(t) {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

export function sessionLabel(s) {
  return s.module_name ? `${s.module_name} (${s.schedule_name})` : s.schedule_name;
}

// A session's `intakes` comes from the QR system as a JSON array or a plain
// comma-separated string; show it as "INT-1, INT-2" ('' when none is set).
export function sessionIntakes(s) {
  const raw = s.intakes;
  if (!raw) return '';
  let list = null;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) list = parsed;
  } catch { /* not JSON — split on commas below */ }
  if (!list) list = String(raw).split(',');
  return list.map(i => String(i).trim()).filter(Boolean).join(', ');
}

function SessionTypeBadge({ value, compact = false }) {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return null;
  const cfg = sessionTypeBadgeMap[key];
  return (
    <span className={`badge rounded-pill ${cfg?.cls || 'badge-inactive'}${compact ? ' sched-month-pill-type' : ''}`} style={{ fontSize: compact ? 10 : 11 }}>
      {cfg?.label || String(value)}
    </span>
  );
}

const WEEK_DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// The column set for week view (Mon-Sun) or day view (the single selected day).
export function scheduleColumns(mode, anchor) {
  if (mode === 'day') {
    return [{ date: anchor, key: ymd(anchor), label: anchor.toLocaleDateString('en-GB', { weekday: 'long' }) }];
  }
  const { monday } = getWeekRange(anchor);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i);
    return { date: d, key: ymd(d), label: WEEK_DAY_LABELS[i] };
  });
}

// Toggle between Day / Week / Month views — shared by the admin board and
// the trainer's own schedule page so both look and behave identically.
export function ViewModeToggle({ mode, onChange }) {
  return (
    <div className="sched-view-toggle" role="tablist" aria-label="Schedule view">
      {['day', 'week', 'month'].map(m => (
        <button
          key={m} type="button" role="tab" aria-selected={mode === m}
          className={`sched-view-btn${mode === m ? ' active' : ''}`}
          onClick={() => onChange(m)}
        >
          {m[0].toUpperCase() + m.slice(1)}
        </button>
      ))}
    </div>
  );
}

// Day/Week card grid — pure presentational, no fetching/socket logic; both
// the admin schedule board and the trainer's own schedule page own their
// data and just pass `sessions` + `onSessionClick` in. `columns` comes from
// scheduleColumns() — one entry for day view, five (Mon-Fri) for week view.
export function ScheduleCardGrid({ columns: days, sessions, loading, onSessionClick, showTrainer }) {
  const byDay = {};
  for (const s of sessions) { (byDay[s.session_date] = byDay[s.session_date] || []).push(s); }
  for (const key of Object.keys(byDay)) byDay[key].sort((a, b) => a.start_time.localeCompare(b.start_time));

  const gridCls = `sched-grid${days.length === 1 ? ' single' : ''}`;

  if (loading) {
    return (
      <div className={gridCls}>
        {days.map(d => <div key={d.key} className="sched-grid-day"><div className="sched-grid-day-head">{d.label} {d.date.getDate()}</div></div>)}
      </div>
    );
  }

  return (
    <div className="sched-grid-wrap">
      <div className={gridCls}>
        {days.map(d => {
          const holiday = getHoliday(d.key);
          return (
          <div key={d.key} className={`sched-grid-day${holiday ? ' holiday' : ''}`}>
            <div className="sched-grid-day-head">{d.label} <span className="sched-grid-day-date">{d.date.getDate()}</span></div>
            {holiday && <div className="sched-grid-day-holiday"><i className="bi bi-star-fill" /> {holiday}</div>}
            {!(byDay[d.key] || []).length && <div className="sched-grid-day-empty">No sessions</div>}
            {(byDay[d.key] || []).map(s => (
              <button
                type="button"
                key={s.id}
                className={`sched-card${s.conflict ? ' conflict' : ''}`}
                onClick={() => onSessionClick(s)}
              >
                <div className="sched-card-title">{sessionLabel(s)}</div>
                {sessionIntakes(s) && <div className="sched-card-meta"><i className="bi bi-mortarboard" /> {sessionIntakes(s)}</div>}
                <div className="sched-card-meta"><i className="bi bi-clock" /> {fmtTime(s.start_time)} – {fmtTime(s.end_time)}</div>
                {showTrainer && <div className="sched-card-meta"><i className="bi bi-person" /> {s.trainer_name || '—'}</div>}
                <div className="sched-card-meta"><i className="bi bi-geo-alt" /> {s.venue_name || '—'}</div>
                <div className="sched-card-badges">
                  <SessionTypeBadge value={s.session_type} />
                  <Badge value={s.status} map={sessionStatusBadgeMap} />
                  {s.conflict && <Badge value={s.conflict_reason} map={conflictBadgeMap} />}
                </div>
              </button>
            ))}
          </div>
          );
        })}
      </div>
    </div>
  );
}

const MONTH_CELL_MAX = 2;

// One day's full session list, opened from a month cell's "+N more" link.
// Selecting a row hands the session up to `onSelect` (the page's roster-modal
// opener) and closes this list — same click target as a card in Day/Week view.
function DaySessionsModal({ date, sessions, showTrainer, onSelect, onClose }) {
  const holiday = getHoliday(ymd(date));
  return (
    <Modal show onClose={onClose} title={date.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}>
      {holiday && <div className="sched-grid-day-holiday mb-3"><i className="bi bi-star-fill" /> {holiday}</div>}
      {sessions.length === 0 ? (
        <EmptyState icon="bi-calendar-x" message="No sessions this day" />
      ) : (
        <div className="d-flex flex-column" style={{ gap: 8 }}>
          {sessions.map(s => (
            <button type="button" key={s.id} className={`sched-card${s.conflict ? ' conflict' : ''}`} onClick={() => onSelect(s)}>
              <div className="sched-card-title">{sessionLabel(s)}</div>
              {sessionIntakes(s) && <div className="sched-card-meta"><i className="bi bi-mortarboard" /> {sessionIntakes(s)}</div>}
              <div className="sched-card-meta"><i className="bi bi-clock" /> {fmtTime(s.start_time)} – {fmtTime(s.end_time)}</div>
              {showTrainer && <div className="sched-card-meta"><i className="bi bi-person" /> {s.trainer_name || '—'}</div>}
              <div className="sched-card-meta"><i className="bi bi-geo-alt" /> {s.venue_name || '—'}</div>
              <div className="sched-card-badges">
                <SessionTypeBadge value={s.session_type} />
                <Badge value={s.status} map={sessionStatusBadgeMap} />
                {s.conflict && <Badge value={s.conflict_reason} map={conflictBadgeMap} />}
              </div>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

// Full-month calendar overview. Each cell shows up to MONTH_CELL_MAX session
// pills directly (time + label, clickable straight into the roster) plus a
// "+N more" link that opens DaySessionsModal for the rest — clicking the date
// number itself still jumps to Day view for that date, where full cards show.
export function MonthGrid({ anchorDate, sessions, loading, onDayClick, onSessionClick, showTrainer }) {
  const weeks = getMonthWeeks(anchorDate);
  const byDay = {};
  for (const s of sessions) { (byDay[s.session_date] = byDay[s.session_date] || []).push(s); }
  for (const key of Object.keys(byDay)) byDay[key].sort((a, b) => a.start_time.localeCompare(b.start_time));

  const [listDay, setListDay] = useState(null); // the day object whose "+more" was clicked

  return (
    <>
    <div className="sched-month">
      <div className="sched-month-row sched-month-headrow">
        {MONTH_DAY_LABELS.map(l => <div key={l} className="sched-month-headcell">{l}</div>)}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="sched-month-row">
          {week.map(day => {
            const daySessions = byDay[day.key] || [];
            const hasConflict = daySessions.some(s => s.conflict);
            const visible = daySessions.slice(0, MONTH_CELL_MAX);
            const extra = daySessions.length - visible.length;
            const holiday = getHoliday(day.key);
            return (
              <div key={day.key} className={`sched-month-cell${day.inMonth ? '' : ' outside'}${hasConflict ? ' conflict' : ''}${holiday ? ' holiday' : ''}`}>
                <button type="button" className="sched-month-date" onClick={() => onDayClick(day.date)} disabled={loading}>
                  {day.date.getDate()}
                </button>
                {holiday && <div className="sched-month-holiday" title={holiday}>{holiday}</div>}
                <div className="sched-month-pills">
                  {visible.map(s => (
                    <button
                      type="button" key={s.id}
                      className={`sched-month-pill${s.conflict ? ' conflict' : ''}`}
                      onClick={() => onSessionClick(s)}
                    >
                      {s.session_type && <SessionTypeBadge value={s.session_type} compact />}
                      <span className="sched-month-pill-time">{fmtTime(s.start_time)}</span> {sessionLabel(s)}{sessionIntakes(s) && ` · ${sessionIntakes(s)}`}
                    </button>
                  ))}
                </div>
                {extra > 0 && (
                  <button type="button" className="sched-month-more" onClick={() => setListDay(day)}>
                    +{extra} more
                  </button>
                )}
                {hasConflict && <span className="sched-month-conflict-dot" title="Conflict" aria-label="Conflict" />}
              </div>
            );
          })}
        </div>
      ))}
    </div>
    {listDay && (
      <DaySessionsModal
        date={listDay.date}
        sessions={byDay[listDay.key] || []}
        showTrainer={showTrainer}
        onSelect={(s) => { setListDay(null); onSessionClick(s); }}
        onClose={() => setListDay(null)}
      />
    )}
    </>
  );
}

// Roster modal for a single session — joins a socket.io room scoped to that
// session_id so present/absent/late counts update live as the QR system writes
// attendance_records, without needing to refresh. `rosterUrl` lets the trainer
// self-service page and the admin schedule board point at their own REST
// endpoints while sharing this same component.
export function RosterModal({ session, rosterUrl, onClose }) {
  const [data, setData] = useState(null); // { session, students, counts }
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [error, setError] = useState('');
  const { sorted, sortKey, sortDir, toggleSort } = useSort(data?.students || [], 'name', 'asc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get(rosterUrl)
      .then(r => { if (!cancelled) setData(r.data.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.message || 'Failed to load roster'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    if (!socket.connected) socket.connect();
    const onConnect = () => {
      socket.emit('join_session', session.id, (res) => {
        if (cancelled) return;
        if (res?.success) { setData(res.data); setLive(true); }
      });
    };
    const onRosterUpdate = (roster) => { if (!cancelled) setData(roster); };
    const onDisconnect = () => setLive(false);

    socket.on('connect', onConnect);
    socket.on('roster_update', onRosterUpdate);
    socket.on('disconnect', onDisconnect);
    if (socket.connected) onConnect();

    return () => {
      cancelled = true;
      socket.emit('leave_session', session.id);
      socket.off('connect', onConnect);
      socket.off('roster_update', onRosterUpdate);
      socket.off('disconnect', onDisconnect);
    };
  }, [session.id, rosterUrl]);

  const counts = data?.counts || { total: 0, present: 0, late: 0, absent: 0, mc: 0 };

  return (
    <Modal show onClose={onClose} size="lg" tightHeader title={
      <span className="d-flex align-items-center gap-2">
        {sessionLabel(session)}
        <SessionTypeBadge value={session.session_type} />
        {live && <span className="badge rounded-pill badge-present" style={{ fontSize: 12 }}>
          <i className="bi bi-broadcast" /> Live
        </span>}
      </span>
    }>
      {!loading && error ? (
        <EmptyState icon="bi-exclamation-triangle" message={error} />
      ) : (
        <>
          <div className="sched-roster-details">
            {session.session_date && <span><i className="bi bi-calendar-event" /> {fmtDate(session.session_date)}</span>}
            {session.start_time && <span><i className="bi bi-clock" /> {fmtTime(session.start_time)} – {fmtTime(session.end_time)}</span>}
            {sessionIntakes(session) && <span><i className="bi bi-mortarboard" /> {sessionIntakes(session)}</span>}
            {session.venue_name && <span><i className="bi bi-geo-alt" /> {session.venue_name}</span>}
            {session.trainer_name && <span><i className="bi bi-person" /> {session.trainer_name}</span>}
          </div>
          <div className="d-flex gap-2 flex-wrap mb-3">
            <span className="badge rounded-pill badge-active" style={{ fontSize: 12 }}>Total: {counts.total}</span>
            <span className="badge rounded-pill badge-present" style={{ fontSize: 12 }}>Present: {counts.present}</span>
            <span className="badge rounded-pill badge-late" style={{ fontSize: 12 }}>Late: {counts.late}</span>
            <span className="badge rounded-pill badge-absent" style={{ fontSize: 12 }}>Absent: {counts.absent}</span>
            <span className="badge rounded-pill badge-mc" style={{ fontSize: 12 }}>MC: {counts.mc}</span>
          </div>
          {!loading && sorted.length === 0 ? (
            <EmptyState icon="bi-people" message="No students recorded for this session" />
          ) : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <table className="trn-table">
                <thead>
                  <tr>
                    <SortTh label="Student" field="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Status" field="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Clock In" field="clock_in_time" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Clock Out" field="clock_out_time" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {loading ? <TableSkeletonRows columns={4} /> : sorted.map(s => (
                    <tr key={s.id}>
                      <td>{s.name || '—'}</td>
                      <td className="sched-roster-status"><Badge value={s.status} map={attendanceBadgeMap} /></td>
                      <td>{fmtDateTime(s.clock_in_time)}</td>
                      <td>{fmtDateTime(s.clock_out_time)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
